/** The setup gate's inputs (backend SETUP-GATE plan D1, D2).
 *
 *  The self-signup `sign_in_url` (and the welcome e-mail) is `/?login_hint=<email>&setup=1`. The sign-in keeps the
 *  query (`finalizeLogin` reloads "/" as it is), and App.js's authenticated branch calls `stashSetupFlag` before
 *  anything renders: MainLayout's redirects would drop the query. The flag goes to sessionStorage per tenant and both
 *  parameters leave the URL. A UI hint only: it decides whether to ask the backend, never what anyone may do. */

const FLAG_PREFIX = 'cp_setup_gate:';

const flagKey = (topId: number | string) => `${FLAG_PREFIX}${topId}`;

/** Moves `?setup=1` into sessionStorage for tenant `topId` and strips `setup` and `login_hint` from the URL.
 *  Idempotent (nothing to do once stripped). Answers whether the URL carried the flag. */
export function stashSetupFlag(topId: number | string | null | undefined, loc: Location = window.location, hist: History = window.history): boolean {
    let params: URLSearchParams;
    try {
        params = new URLSearchParams(loc.search || '');
    } catch (_e) {
        return false;
    }
    if (!params.has('setup') && !params.has('login_hint')) {
        return false;
    }
    const flagged = params.get('setup') === '1';
    if (flagged && topId != null) {
        try {
            window.sessionStorage.setItem(flagKey(topId), '1');
        } catch (_e) {
            // Storage refused (private mode): the gate still asks for a user without permissions.
        }
    }
    params.delete('setup');
    params.delete('login_hint');
    const query = params.toString();
    try {
        hist.replaceState(hist.state, '', `${loc.pathname}${query ? `?${query}` : ''}${loc.hash || ''}`);
    } catch (_e) {
        // Nothing to strip with.
    }
    return flagged;
}

export function hasSetupFlag(topId: number | string | null | undefined): boolean {
    if (topId == null) {
        return false;
    }
    try {
        return window.sessionStorage.getItem(flagKey(topId)) === '1';
    } catch (_e) {
        return false;
    }
}

export function clearSetupFlag(topId: number | string | null | undefined) {
    if (topId == null) {
        return;
    }
    try {
        window.sessionStorage.removeItem(flagKey(topId));
    } catch (_e) {
        // Nothing stored.
    }
}

export type PermissionState = 'none' | 'some' | 'unknown';

const grantsAny = (byScope: any): boolean => !!byScope && typeof byScope === 'object'
    && Object.values(byScope).some((actions) => !!actions && typeof actions === 'object' && Object.values(actions as object).some((v) => !!v));

/** The user's permissions (`user.permissions`, the my_permissions cache) as the gate reads them, the way `hasPerm`
 *  does: `unknown` when not loaded (hasPerm then allows everything), `some` when access groups are off, with full
 *  access or any grant, `none` otherwise -- the empty dashboard of an admin whose setup has not run yet. */
export function permissionState(permissions: any): PermissionState {
    if (!permissions || typeof permissions !== 'object') {
        return 'unknown';
    }
    if (!permissions.access_groups_enabled || permissions.full_access) {
        return 'some';
    }
    return grantsAny(permissions.scoped_all_tags) || grantsAny(permissions.perms) ? 'some' : 'none';
}

/** Whether the gate asks the backend: a user without any permission, or one who came from the sign-up link while
 *  their permissions are not known. A user with permissions is never gated. */
export function shouldCheckSetup(permissions: any, flagged: boolean): boolean {
    const state = permissionState(permissions);
    return state === 'none' || (flagged && state === 'unknown');
}
